/**
 * Geography landing - the consolidated Geographic Reference Data admin tab
 * (WPS-222). One place for every geo table: zones, plants, PSA mappings,
 * postal codes, the US Census reference tables, product availability and the
 * (Product Template x Geo Zone) claim-routing ownership, plus an address
 * lookup tester. Follows the Products landing pattern (Tab + Outlet); the tab
 * list is filtered by the caller's codenames and the index redirects to the
 * first visible tab.
 */

import { useMemo } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/admin/components/app-shell'
import { Tab } from '@/shared/components/ui/tab'
import { usePermissions } from '@/admin/hooks/use-permissions'

const GEOGRAPHY_BASE = '/manage/geography'

function useGeographyTabs() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  return useMemo(() => {
    const reference = can('geo-zones.view')
    const assignments = can('geo-assignments.view')
    const products = can('products.view.global')
    const tabs: { label: string; path: string }[] = []
    if (reference) {
      tabs.push(
        { label: t('geography.tabs.zones', 'Zones'), path: `${GEOGRAPHY_BASE}/zones` },
        { label: t('geography.tabs.plants', 'Plants'), path: `${GEOGRAPHY_BASE}/plants` },
        { label: t('geography.tabs.psa', 'PSA Mappings'), path: `${GEOGRAPHY_BASE}/psa` },
        {
          label: t('geography.tabs.postalCodes', 'Postal Codes'),
          path: `${GEOGRAPHY_BASE}/postal-codes`,
        },
        {
          label: t('geography.tabs.reference', 'US Reference'),
          path: `${GEOGRAPHY_BASE}/reference`,
        },
      )
    }
    if (reference && products) {
      tabs.push({
        label: t('geography.tabs.availability', 'Availability'),
        path: `${GEOGRAPHY_BASE}/availability`,
      })
    }
    if (assignments) {
      tabs.push({
        label: t('geography.tabs.assignments', 'Assignments'),
        path: `${GEOGRAPHY_BASE}/assignments`,
      })
    }
    if (reference) {
      tabs.push({ label: t('geography.tabs.lookup', 'Lookup'), path: `${GEOGRAPHY_BASE}/lookup` })
    }
    return tabs
  }, [can, t])
}

export function GeographyLandingPage() {
  const { t } = useTranslation()
  const tabs = useGeographyTabs()
  const location = useLocation()
  const onIndex = location.pathname.replace(/\/$/, '') === GEOGRAPHY_BASE

  if (onIndex && tabs.length > 0) {
    return <Navigate to={tabs[0].path} replace />
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        <h1 className="clara-title mb-6">{t('geography.title', 'Geography')}</h1>
        {tabs.length > 0 ? <Tab tabs={tabs} /> : null}
        <Outlet />
      </main>
    </AppShell>
  )
}
